import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { restClient } from '../../../api/rest-client';
import type { SkillsReportResponse, SkillsReportGroup } from '../../../types/skills';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import NeedsAttentionCallout from './NeedsAttentionCallout';
import SkillSectionHeatmap from './SkillSectionHeatmap';
import ScoreDistributionChart from './ScoreDistributionChart';
import RiderSkillsList from './RiderSkillsList';
import {
  computeGroupAverage,
  computeFocusAreas,
  computeScoreBandDistribution,
  computeNeedsAttentionRiders,
  sortRidersForDisplay,
  getGroupFromReport,
  getDefaultGroup,
} from '../../../utils/groupSkillsUtils';

// Session cache for skills report
const SKILLS_CACHE_KEY = 'blueclipboard_skills_report_cache';
const SKILLS_CACHE_TIMESTAMP_KEY = 'blueclipboard_skills_report_cache_timestamp';
const SKILLS_CACHE_VERSION_KEY = 'blueclipboard_skills_report_cache_version';
const SKILLS_CACHE_VERSION = '1';
const SKILLS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getSkillsReportFromCache(): SkillsReportResponse | null {
  try {
    const version = sessionStorage.getItem(SKILLS_CACHE_VERSION_KEY);
    if (version !== SKILLS_CACHE_VERSION) {
      sessionStorage.removeItem(SKILLS_CACHE_KEY);
      sessionStorage.removeItem(SKILLS_CACHE_TIMESTAMP_KEY);
      sessionStorage.setItem(SKILLS_CACHE_VERSION_KEY, SKILLS_CACHE_VERSION);
      return null;
    }
    const timestamp = sessionStorage.getItem(SKILLS_CACHE_TIMESTAMP_KEY);
    if (!timestamp || Date.now() - parseInt(timestamp, 10) > SKILLS_CACHE_TTL) {
      return null;
    }
    const cached = sessionStorage.getItem(SKILLS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
}

function setSkillsReportCache(data: SkillsReportResponse): void {
  try {
    sessionStorage.setItem(SKILLS_CACHE_KEY, JSON.stringify(data));
    sessionStorage.setItem(SKILLS_CACHE_TIMESTAMP_KEY, Date.now().toString());
    sessionStorage.setItem(SKILLS_CACHE_VERSION_KEY, SKILLS_CACHE_VERSION);
  } catch (err) {
    console.warn('[GroupSkillsCard] Failed to cache skills report:', err);
  }
}

/** Clear the session cache so the next fetch hits the API */
export function clearSkillsReportCache(): void {
  try {
    sessionStorage.removeItem(SKILLS_CACHE_KEY);
    sessionStorage.removeItem(SKILLS_CACHE_TIMESTAMP_KEY);
  } catch { /* ignore */ }
}

interface GroupSkillsCardProps {
  coachGroup: string | null;
  isAdmin: boolean;
  onRiderSelect: (riderId: string) => void;
  /** Bump this value to force a fresh re-fetch (cache bypass) */
  refreshTrigger?: number;
}

/**
 * Group-level skills overview card for the Dashboard.
 * Collapsed by default — header shows group name/dropdown, average %, and chevron.
 * When expanded: needs attention, summary stats, heatmap, distribution chart, rider list.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 3.1, 3.2, 3.3, 3.4, 3.5,
 *              9.1, 9.2, 9.3, 9.4, 10.1, 10.2, 11.1, 11.2, 11.7, 11.8, 11.9, 11.10
 */
export default function GroupSkillsCard({
  coachGroup,
  isAdmin,
  onRiderSelect,
  refreshTrigger,
}: GroupSkillsCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skillsReport, setSkillsReport] = useState<SkillsReportResponse | null>(null);

  const fetchReport = useCallback(async (group?: string) => {
    setLoading(true);
    setError(null);

    // Check session cache first (only use cache when no group filter)
    if (!group) {
      const cached = getSkillsReportFromCache();
      if (cached) {
        setSkillsReport(cached);
        setLoading(false);
        return;
      }
    }

    try {
      const data = await restClient.getSkillsReport(group);
      if (!group) {
        setSkillsReportCache(data);
      }
      setSkillsReport(data);
    } catch {
      setError('Failed to load skills report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount so header stats (group name, mastery %) are visible when collapsed
  useEffect(() => {
    const group = !isAdmin && coachGroup ? coachGroup : undefined;
    fetchReport(group);
  }, [fetchReport, isAdmin, coachGroup]);

  // Re-fetch (bypassing cache) when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      clearSkillsReportCache();
      const group = !isAdmin && coachGroup ? coachGroup : undefined;
      fetchReport(group);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Whether to show the group selector dropdown
  // Admins always get it; coaches without a group also get it as fallback
  const showGroupDropdown = isAdmin || !coachGroup;

  // Auto-select group when report loads
  useEffect(() => {
    if (!skillsReport) return;

    if (!isAdmin && coachGroup) {
      // Coach with a known group — lock to that group
      setSelectedGroup(coachGroup);
    } else if (!isAdmin) {
      // Coach-without-group — default to lowest group_order
      const defaultGroup = getDefaultGroup(skillsReport.groups);
      if (defaultGroup) {
        setSelectedGroup(defaultGroup.group_name);
      }
    }
    // Admin: leave selectedGroup null so "Choose a group" shows
  }, [skillsReport, isAdmin, coachGroup]);

  // Derive current group data
  const currentGroup: SkillsReportGroup | undefined = useMemo(() => {
    if (!skillsReport || !selectedGroup) return undefined;
    return getGroupFromReport(skillsReport, selectedGroup);
  }, [skillsReport, selectedGroup]);

  // Compute derived data
  const groupAverage = useMemo(
    () => (currentGroup?.skill_averages ? computeGroupAverage(currentGroup.skill_averages) : 0),
    [currentGroup],
  );

  const focusAreas = useMemo(
    () => (currentGroup?.skill_averages ? computeFocusAreas(currentGroup.skill_averages) : []),
    [currentGroup],
  );

  const scoreBandDistribution = useMemo(
    () => (currentGroup ? computeScoreBandDistribution(currentGroup.riders) : { belowTarget: 0, mostAtTarget: 0, allAtTarget: 0 }),
    [currentGroup],
  );

  const needsAttentionRiders = useMemo(
    () => (currentGroup ? computeNeedsAttentionRiders(currentGroup.riders) : []),
    [currentGroup],
  );

  const sortedRiders = useMemo(
    () => (currentGroup ? sortRidersForDisplay(currentGroup.riders) : []),
    [currentGroup],
  );

  return (
    <section className="rounded-xl border border-gray-200 bg-white lg:col-span-2">
      {/* Card header */}
      <div className="p-4 pb-0">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-primary-900">Skills Assessment</h2>
          <Link
            to="/skills"
            className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            View Training →
          </Link>
        </div>
        <p className="text-base text-gray-500 mt-1">Group-level skills overview</p>
      </div>

      {/* Collapsible group selector */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
        aria-expanded={expanded}
      >
        {showGroupDropdown && skillsReport ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === ' ') e.stopPropagation(); }}
          >
            <select
              value={selectedGroup || ''}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base font-semibold text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Select group"
            >
              <option value="">Choose a group</option>
              {skillsReport.groups.map((g) => (
                <option key={g.group_name} value={g.group_name}>
                  {g.group_name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="text-base font-semibold text-gray-800">
            {selectedGroup || 'Group Skills Overview'}
          </p>
        )}
        {!loading && currentGroup && (
          <p className="text-sm font-medium text-gray-500 mt-1 text-center">
            {Math.round(groupAverage)}% mastery of all skills
          </p>
        )}
        <div className="flex justify-center mt-1">
          <svg
            className={`h-6 w-6 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 space-y-6">
          {/* Loading state */}
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
              <p className="text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => {
                  const group = !isAdmin && coachGroup ? coachGroup : undefined;
                  fetchReport(group);
                }}
                className="mt-2 min-h-[44px] min-w-[44px] rounded-md bg-red-100 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                Retry
              </button>
            </div>
          )}

          {/* Data loaded successfully */}
          {!loading && !error && currentGroup && (
            <>
              {/* Needs Attention callout — prominent, before other content */}
              <NeedsAttentionCallout
                riders={needsAttentionRiders}
                onRiderSelect={onRiderSelect}
              />

              {/* Summary stats — stack vertically on mobile */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg bg-gray-50 p-3">
                  <p className="text-sm text-gray-500">Riders</p>
                  <p className="text-lg font-semibold text-gray-800">{currentGroup.riders.length}</p>
                </div>

                {focusAreas.length > 0 && (
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="flex justify-between items-baseline">
                      <p className="text-sm text-gray-500">Focus Areas</p>
                      <p className="text-sm text-gray-400">Weighted Score</p>
                    </div>
                    <div className="mt-1 space-y-0.5 text-base text-gray-700">
                      {focusAreas.map((skill) => (
                        <div key={skill.skill_id} className="flex justify-between">
                          <span className="truncate mr-2">{skill.skill_name}</span>
                          <span className="text-sm font-medium text-gray-500">{skill.weighted_skill_score}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Skill Section Heatmap */}
              {currentGroup.skill_averages && (
                <SkillSectionHeatmap
                  skillAverages={currentGroup.skill_averages}
                  focusAreas={focusAreas}
                />
              )}

              {/* Score Distribution Chart */}
              <ScoreDistributionChart distribution={scoreBandDistribution} />

              {/* Rider Skills List */}
              <RiderSkillsList
                riders={sortedRiders}
                onRiderSelect={onRiderSelect}
              />

              {/* View Full Report link — admin only */}
              {isAdmin && (
                <div className="pt-2 text-center">
                  <Link
                    to="/admin/reports/skills"
                    className="inline-block min-h-[44px] min-w-[44px] rounded-md px-4 py-2 text-sm font-medium text-blue-700 hover:text-blue-500 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  >
                    View Full Report →
                  </Link>
                </div>
              )}
            </>
          )}

          {/* No riders in group */}
          {!loading && !error && currentGroup && currentGroup.riders.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-500">
              No riders in this group.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
